import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StatusDot from './StatusDot';
import { formatTimeShort } from './dateUtils';

/**
 * Quieter appointment chips: soft status-tinted fill, 8px radius, no left
 * border (the 3px border repeated the old nav-selection motif). The one
 * exception is in-progress, which wears the solid brand accent so the
 * live session pops. Completed/cancelled recede via opacity.
 */
const AppointmentChip = ({ appt, onClick, compact = false, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const accent =
    appt.status === 'no_show'
      ? theme.palette.error.main
      : appt.status === 'in_progress'
      ? theme.palette.primary.main
      : appt.status === 'completed' || appt.status === 'confirmed'
      ? theme.palette.success.main
      : appt.status === 'cancelled'
      ? theme.palette.text.disabled
      : theme.palette.info.main;

  const live = appt.status === 'in_progress';
  const dimmed = appt.status === 'completed' || appt.status === 'cancelled';
  const struck = appt.status === 'cancelled';

  const bg = live ? theme.palette.primary.main : alpha(accent, isDark ? 0.16 : 0.1);
  const bgHover = live ? theme.palette.primary.main : alpha(accent, isDark ? 0.26 : 0.18);
  const ink = live ? theme.palette.primary.contrastText : theme.palette.text.primary;
  const subInk = live ? alpha(theme.palette.primary.contrastText, 0.72) : theme.palette.text.secondary;

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick(appt);
      }}
      data-testid={`appointment-${appt.appointment_id}`}
      sx={{
        cursor: 'pointer',
        borderRadius: '8px',
        px: 1.125,
        py: compact ? 0.25 : 0.75,
        bgcolor: bg,
        color: ink,
        opacity: dimmed ? 0.62 : 1,
        overflow: 'hidden',
        transition: 'background-color 120ms ease, opacity 120ms ease',
        '&:hover': { bgcolor: bgHover, opacity: 1 },
        ...sx,
      }}
    >
      {compact ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {!live && <StatusDot status={appt.status} />}
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 600, fontSize: '0.7rem', textDecoration: struck ? 'line-through' : 'none' }}
          >
            {formatTimeShort(appt.start_time)} {appt.service_name}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {appt.status === 'no_show' && <StatusDot status={appt.status} />}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, lineHeight: 1.25, textDecoration: struck ? 'line-through' : 'none' }}
              noWrap
            >
              {appt.service_name || 'Appointment'}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', lineHeight: 1.25, fontSize: '0.7rem', color: subInk }}
            noWrap
          >
            {formatTimeShort(appt.start_time)} · {appt.member_name}
            {live ? ' · now' : ''}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AppointmentChip;
